import { SPEAKER_TEST_SOUND_SRC } from '../constants/device.constants'
import { getAudioContextCtor } from '../utils/device-permissions'
import { notifySpeakerRoutingFailure } from './audio-output.service'

type HTMLAudioElementWithSinkId = HTMLAudioElement & {
  setSinkId?: (sinkId: string) => Promise<void>
}

async function playFileSound(speakerId?: string): Promise<boolean> {
  const audio = new Audio(SPEAKER_TEST_SOUND_SRC) as HTMLAudioElementWithSinkId
  audio.preload = 'auto'

  if (typeof audio.setSinkId === 'function' && speakerId) {
    try {
      await audio.setSinkId(speakerId)
    } catch {
      // Falling through to the default sink means the test tone plays out of
      // the laptop while the dropdown says "headset" — the single most
      // misleading thing this control can do, because it reads as proof the
      // choice took.
      notifySpeakerRoutingFailure(speakerId)
    }
  }

  try {
    audio.currentTime = 0
    await audio.play()
    await new Promise<void>((resolve) => {
      const cleanup = () => {
        audio.removeEventListener('ended', cleanup)
        audio.removeEventListener('error', cleanup)
        resolve()
      }
      audio.addEventListener('ended', cleanup)
      audio.addEventListener('error', cleanup)
    })
    return true
  } catch {
    return false
  }
}

async function playOscillatorFallback(speakerId?: string): Promise<void> {
  const AudioCtor = getAudioContextCtor()
  if (!AudioCtor) return

  const ctx = new AudioCtor()
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  gain.gain.value = 0.0001
  osc.type = 'sine'
  osc.frequency.value = 740
  osc.connect(gain)

  const outAudio = new Audio() as HTMLAudioElementWithSinkId
  if (typeof outAudio.setSinkId === 'function' && speakerId) {
    try {
      await outAudio.setSinkId(speakerId)
      const mediaDest = ctx.createMediaStreamDestination()
      gain.connect(mediaDest)
      outAudio.srcObject = mediaDest.stream
      await outAudio.play().catch(() => {})
    } catch {
      notifySpeakerRoutingFailure(speakerId)
      gain.connect(ctx.destination)
    }
  } else {
    gain.connect(ctx.destination)
  }

  const now = ctx.currentTime
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22)
  osc.start(now)
  osc.stop(now + 0.24)

  setTimeout(() => {
    ctx.close().catch(() => {})
  }, 350)
}

export const speakerTestService = {
  async play(speakerId?: string): Promise<void> {
    const ok = await playFileSound(speakerId)
    if (!ok) await playOscillatorFallback(speakerId)
  },
}
